import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowLineDownIcon,
  CalendarIcon,
  CurecallButton,
  DialogueBubbleIcon,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  FileTextIcon,
  NoteIcon,
} from '@honestica/curecall-ui'
import { TPatient } from 'src/types/patient'
import { TPatientFollowup } from 'src/types/followup'
import {
  useCurrentProject,
  WithFeatureEnabled,
  WithPermission,
  WithSpecialAuthorization,
} from 'src/providers/current-project.provider'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useModals } from 'src/providers/modals.provider'
import { SMSModal } from 'src/components/sms-modal/sms-modal.component'
import { EndFollowupModal } from './end-followup-modal.component'
import { PatientDocumentsModal } from './patient-documents-modal.component'
import { downloadPatientReport } from './patient-report.helper'
import { useNotification } from 'src/providers/notification.provider'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { EPermissionSpecialAuthorization } from 'src/pages/configuration/permissions/_components/permissions.type'

const PatientFollowupActionBar = ({
  patient,
  currentPatientFollowup,
}: {
  patient: TPatient
  currentPatientFollowup: TPatientFollowup
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { notify } = useNotification()
  const { project } = useCurrentProject()
  const { openFollowupModal, openMedicalTeamCommentsModal } = useModals()

  const [isSmsModalOpened, setIsSmsModalOpened] = useState<boolean>(false)
  const [isDocumentsModalOpened, setIsDocumentsModalOpened] = useState<boolean>(false)
  const [isEndFollowupModalOpened, setIsEndFollowupModalOpened] = useState<boolean>(false)

  useEffect(() => {
    if (searchParams.get('isSmsModaleOpen')) return setIsSmsModalOpened(true)
    if (searchParams.get('isMedicalTeamCommentOpen'))
      return openMedicalTeamCommentsModal({
        patientId: patient.id,
        patientFollowup: currentPatientFollowup,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const isPatientFollowupActive = !currentPatientFollowup?.endReason

  const handleDownloadPatientReport = async () => {
    try {
      downloadPatientReport({
        projectId: project.id,
        patient,
        patientFollowupId: currentPatientFollowup.id,
      })
    } catch (e: any) {
      notify({
        title: t('pages.patient-page.download-patient-report-error-title'),
        message: e,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  return (
    <div className="c-my-4 c-flex c-gap-6">
      {isPatientFollowupActive && (
        <WithPermission
          requiredSubject={EPermissionGrantSubjects.PATIENT}
          requiredAction={EPermissionGrantActions.UPDATE}
        >
          <CurecallButton
            type="button"
            onClick={() => {
              openFollowupModal({
                patientId: patient.id,
                patientFollowupIdToEdit: currentPatientFollowup.id,
              })
            }}
            appearance={ECurecallButtonAppearance.SECONDARY}
            hasIconAtStart
            iconType={<CalendarIcon className="tw-w-4 tw-h-max" />}
          >
            <span className="tw-ml-1">
              {t('components.patient.patient-file.body.followup-details.action-bar.add-appointment')}
            </span>
          </CurecallButton>
        </WithPermission>
      )}
      <CurecallButton
        type="button"
        onClick={() => setIsSmsModalOpened(true)}
        appearance={ECurecallButtonAppearance.SECONDARY}
        hasIconAtStart
        iconType={<DialogueBubbleIcon className="tw-w-4 tw-h-max" />}
      >
        <span className="tw-ml-1">
          {t('components.patient.patient-file.body.followup-details.action-bar.see-sms')}
        </span>
      </CurecallButton>
      {isSmsModalOpened && (
        <SMSModal
          isOpen={true}
          onToggle={() => {
            setIsSmsModalOpened(false)
            const currentParams = new URLSearchParams(searchParams.toString())
            currentParams.delete('isSmsModaleOpen')
            navigate(`?${currentParams}`)
          }}
          patient={patient}
          currentPatientFollowup={currentPatientFollowup}
        />
      )}

      <WithFeatureEnabled featuresAccepted={['patient-documents']}>
        <WithSpecialAuthorization
          requiredSpecialAuthorization={EPermissionSpecialAuthorization.CAN_VIEW_SENSITIVE_DATA}
        >
          <CurecallButton
            type="button"
            onClick={() => setIsDocumentsModalOpened(true)}
            appearance={ECurecallButtonAppearance.SECONDARY}
            hasIconAtStart
            iconType={<FileTextIcon className="tw-w-4 tw-h-max" />}
          >
            {t('components.patient.patient-file.body.followup-details.action-bar.see-documents')}
          </CurecallButton>
          {isDocumentsModalOpened && (
            <PatientDocumentsModal
              patientFollowupId={currentPatientFollowup.id}
              onToggle={() => setIsDocumentsModalOpened(false)}
            />
          )}
        </WithSpecialAuthorization>
      </WithFeatureEnabled>

      <WithPermission
        requiredSubject={EPermissionGrantSubjects.PATIENT}
        requiredAction={EPermissionGrantActions.GET}
      >
        <CurecallButton
          type="button"
          onClick={() => {
            openMedicalTeamCommentsModal({
              patientId: patient.id,
              patientFollowup: currentPatientFollowup,
            })
          }}
          appearance={ECurecallButtonAppearance.SECONDARY}
          hasIconAtStart
          iconType={<NoteIcon className="tw-w-4 tw-h-max" />}
        >
          {t('components.patient.patient-file.body.followup-details.action-bar.see-medical-team-comment')}
        </CurecallButton>
      </WithPermission>

      {currentPatientFollowup?.reportAvailable && (
        <WithSpecialAuthorization
          requiredSpecialAuthorization={EPermissionSpecialAuthorization.CAN_VIEW_PATIENT_IDENTITIES}
        >
          <CurecallButton
            type="button"
            onClick={handleDownloadPatientReport}
            appearance={ECurecallButtonAppearance.SECONDARY}
            hasIconAtStart
            iconType={<ArrowLineDownIcon className="tw-w-4" />}
          >
            {t('components.patient.patient-file.body.followup-details.action-bar.download-report')}
          </CurecallButton>
        </WithSpecialAuthorization>
      )}

      {isPatientFollowupActive && (
        <WithFeatureEnabled featuresAccepted={['CAN_END_PATIENT_FOLLOWUP']}>
          <CurecallButton
            onClick={() => setIsEndFollowupModalOpened(true)}
            appearance={ECurecallButtonAppearance.CANCEL}
            className="c-ml-auto"
          >
            {t('components.patient.patient-file.body.followup-details.action-bar.end-this-followup')}
          </CurecallButton>
        </WithFeatureEnabled>
      )}
      {isEndFollowupModalOpened && (
        <EndFollowupModal
          onToggle={() => {
            setIsEndFollowupModalOpened(false)
          }}
          patient={patient}
          currentPatientFollowup={currentPatientFollowup}
        />
      )}
    </div>
  )
}

export default PatientFollowupActionBar
